import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AreaTrend, DonutAllocation, CHART_COLORS } from "@/components/Charts";
import { fmtUSD, fmtPct } from "@/lib/format";
import { toastError } from "@/lib/toastError";
import { Calculator, TrendingUp } from "lucide-react";

const RISK_LEVELS = [
  ["Very Conservative", 1], ["Conservative", 3], ["Moderate", 5],
  ["Moderately Aggressive", 7], ["Aggressive", 9], ["Very Aggressive", 10],
];

export const ProjectionCalculator = ({ halal = false }) => {
  const [level, setLevel] = useState(2); // index into RISK_LEVELS
  const [initial, setInitial] = useState(10000);
  const [monthly, setMonthly] = useState(500);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const calc = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invest/projection", {
        risk_score: RISK_LEVELS[level][1], initial_deposit: Number(initial), monthly: Number(monthly), halal,
      });
      setResult(data);
    } catch (e) { toastError("Couldn't run projection", e); } finally { setBusy(false); }
  };

  const allocData = result ? result.allocation.map((a) => ({ name: a.symbol, value: Math.round(a.weight * 100) })) : [];

  return (
    <Card className="p-5 shadow-premium card-premium" data-testid="projection-calculator">
      <div className="flex items-center gap-2 text-sm font-medium mb-4"><Calculator className="h-4 w-4 text-[color:var(--tharwy-lime)]" />Portfolio Performance Projection</div>
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Risk tolerance</Label>
            <div className="grid grid-cols-2 gap-2">
              {RISK_LEVELS.map(([label], i) => (
                <button key={label} onClick={() => setLevel(i)}
                  className={`text-start rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${level === i ? "bg-secondary border-[color:var(--tharwy-lime)]" : "hover:bg-accent"}`}
                  data-testid={`proj-risk-${i}`}>{label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Initial (USD)</Label><Input type="number" value={initial} onChange={(e) => setInitial(e.target.value)} data-testid="proj-initial" /></div>
            <div className="space-y-1.5"><Label>Monthly (USD)</Label><Input type="number" value={monthly} onChange={(e) => setMonthly(e.target.value)} data-testid="proj-monthly" /></div>
          </div>
          <Button className="w-full" onClick={calc} disabled={busy} data-testid="proj-calculate">{busy ? "Calculating..." : "Project performance"}</Button>
          {result && (
            <div className="flex items-center gap-3 pt-1">
              <div className="w-24"><DonutAllocation data={allocData} height={96} /></div>
              <div className="flex-1 space-y-1">
                {result.allocation.slice(0, 4).map((a, i) => (
                  <div key={`${a.symbol}-${i}`} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />{a.symbol}</span>
                    <span className="numeric">{Math.round(a.weight * 100)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          {result ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(result.results).map(([period, r]) => (
                  <div key={period} className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">{period} projected</div>
                    <div className="numeric text-lg font-semibold">{fmtUSD(r.ending_balance)}</div>
                    <div className="text-xs text-emerald-600 numeric">{fmtPct(r.total_return_pct)} total</div>
                  </div>
                ))}
              </div>
              <div className="rounded-lg bg-secondary/60 p-2"><AreaTrend data={result.series} dataKey="value" xKey="label" color="#5C7020" height={140} /></div>
              <p className="text-[11px] text-muted-foreground">Hypothetical, net of {result.fee_pct}% fee. {result.profile} profile · {result.expected_return}% expected return. Past performance is not indicative of future results.</p>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center rounded-lg border border-dashed p-8">
              <TrendingUp className="h-8 w-8 text-muted-foreground mb-2" />
              <div className="text-sm font-medium">See how your money could grow</div>
              <div className="text-xs text-muted-foreground mt-1">Pick a risk level and project 1–10 year returns</div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
